import { useEffect, useState } from 'react';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { CustomContext } from '@/graphql/client';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import {
  type ProductCardFragment,
  type ProductFilterInput,
} from '@/graphql/saleor/api';
import { fetchProductBusinessData } from '@/integrations/orderApp';
import { fetchProductCardsData } from '@/integrations/saleor/api';
import {
  CARDS_IMAGE_SIZE,
  CAROUSEL_FETCH_LIMIT,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { skusToSlugs } from '@/lib/product';
import type { CtaType } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';

export interface ProductCardFragmentWithVariantCta extends ProductCardFragment {
  launch_date?: number;
  variantCTA?: CtaType;
}

interface UseFetchProductsWithCtaProps {
  excludeSlug?: string;
  existProductsData?: ProductCardFragmentWithVariantCta[];
  filter: ProductFilterInput | undefined;
  orderedSlugs?: string[];
  skip?: boolean; // skip auto fetch
  skipFetchCta?: boolean;
}

export const reorderProductsBySlugs = (
  products: ProductCardFragmentWithVariantCta[],
  orderedSlugs: string[],
): ProductCardFragmentWithVariantCta[] => {
  if (!Array.isArray(orderedSlugs) || orderedSlugs.length === 0) {
    return products;
  }

  return orderedSlugs
    .map((slug) => products.find((product) => product.slug === slug))
    .filter(Boolean) as ProductCardFragmentWithVariantCta[];
};

export const useFetchProductsWithCta = ({
  excludeSlug,
  existProductsData = [],
  filter,
  orderedSlugs = [],
  skip = false,
  skipFetchCta = false,
}: UseFetchProductsWithCtaProps) => {
  const [products, setProducts] =
    useState<ProductCardFragmentWithVariantCta[]>(existProductsData);
  const { query } = useRegionProvider();

  useEffect(() => {
    if (existProductsData && existProductsData.length > 0) {
      setProducts(existProductsData);
    }
  }, [existProductsData]);

  const getProducts = useMemoizedFn(async () => {
    const normalizedFilter = {
      ...filter,
      slugs: skusToSlugs(filter?.slugs ?? []),
    };

    const { data, errors } = await fetchProductCardsData(
      {
        first: CAROUSEL_FETCH_LIMIT,
        thumbnailSize: CARDS_IMAGE_SIZE,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        ...query,
        filter: normalizedFilter,
      },
      CustomContext({
        disableErrorNotification: true,
      }),
    );

    const isDataReturned = !!data.products?.edges.length;

    if (!errors && isDataReturned) {
      const fetchedProducts = mapEdgesToItems(data.products);

      // If an excludeSlug is provided, filter it out
      const filteredProducts = excludeSlug
        ? fetchedProducts.filter((product) => product.slug !== excludeSlug)
        : fetchedProducts;

      // Reorder based on the saved SKUs order coming from localstorage
      const orderedProducts = reorderProductsBySlugs(
        filteredProducts,
        orderedSlugs,
      );

      setProducts(orderedProducts);
    }
  });

  useEffect(() => {
    if (products.length || !filter || skip) {
      return;
    }
    getProducts();
  }, [products, filter, excludeSlug, skip]);

  useEffect(() => {
    (async () => {
      const isCtaSet = products.some(({ variantCTA }) => !!variantCTA);

      if (!products.length || isCtaSet || skipFetchCta) {
        return;
      }

      const productsSkus = products
        .map((item) => item.defaultVariant?.sku)
        .flatMap((sku) => (sku ? [sku] : []));

      const orderAppData = await fetchProductBusinessData(
        productsSkus,
        query.channel,
      );

      const productsWithVariantCta = getProductsWithVariantCta(
        orderAppData,
        products,
      );

      const orderedProducts = reorderProductsBySlugs(
        productsWithVariantCta,
        orderedSlugs,
      );

      setProducts(orderedProducts);
    })();
  }, [products, skipFetchCta]);

  const refetch = () => {
    if (!filter) {
      return;
    }
    getProducts();
  };

  return { products, refetch };
};

export const getProductsWithVariantCta = (
  orderAppData: OrderAppProductVariantsBusinessDetailsQuery,
  products: ProductCardFragment[],
) => {
  const productsWithVariantCta: ProductCardFragmentWithVariantCta[] = [];

  if (orderAppData.productVariants) {
    for (const orderAppItem of orderAppData.productVariants) {
      const foundProduct = products.find(
        (productItem) => productItem.defaultVariant?.sku === orderAppItem.sku,
      );
      if (foundProduct) {
        productsWithVariantCta.push({
          ...foundProduct,
          variantCTA: orderAppItem.variantCTA,
        });
      }
    }
  }

  return productsWithVariantCta;
};
